import { HashtagInfo, HashtagPosts, HashtagRelated } from 'shared/models';
import { atom, PrimitiveAtom } from 'jotai';
import { pendingQueue } from 'shared/utils/pending-queue';
import { ITopicUnavailableDetail } from 'shared/models';
import { PromiseOrValue } from 'shared/utils/promise-or-value';
import { safeAtomFamily } from 'shared/utils/safe-atom-family';

type HashtagInfoStore = { [key: string]: HashtagInfo | ITopicUnavailableDetail | undefined };
type HashtagPostsStore = { [key: string]: PromiseOrValue<HashtagPosts> | undefined };
type HashtagRelatedStore = { [key: string]: PromiseOrValue<HashtagRelated> | undefined };

export const $HashtagInfoStore: PrimitiveAtom<HashtagInfoStore> = atom<HashtagInfoStore>({});
export const $HashtagPopularPostStore: PrimitiveAtom<HashtagPostsStore> = atom<HashtagPostsStore>({});
export const $HashtagRecentPostStore: PrimitiveAtom<HashtagPostsStore> = atom<HashtagPostsStore>({});
export const $HashtagRelatedStore: PrimitiveAtom<HashtagRelatedStore> = atom<HashtagRelatedStore>({});

// HashtagInfo HashtagPopularPost HashtagRecentPost HashtagRelated
export const $HashtagInfo = safeAtomFamily((hashtagId: string) => {
  const a = atom(
    (get) => {
      const data = get($HashtagInfoStore)[hashtagId];
      if (!data) {
        // eslint-disable-next-line @typescript-eslint/no-throw-literal
        throw new Promise((resolve) => pendingQueue.push(resolve));
      }
      return data;
    },
    (get, set, input: HashtagInfo | ITopicUnavailableDetail) => {
      const old = get($HashtagInfoStore);
      set($HashtagInfoStore, { ...old, [hashtagId]: input });
    },
  );
  a.debugLabel = `$HashtagInfo+${encodeURIComponent(hashtagId)}`;
  return a;
});

export const $HashtagPopularPost = safeAtomFamily((hashtagId: string) => {
  const a = atom(
    (get) => {
      const data = get($HashtagPopularPostStore)[hashtagId];
      if (!data) {
        // eslint-disable-next-line @typescript-eslint/no-throw-literal
        throw new Promise((resolve) => pendingQueue.push(resolve));
      }
      return data;
    },
    (get, set, input: PromiseOrValue<HashtagPosts>) => {
      const old = get($HashtagPopularPostStore);
      set($HashtagPopularPostStore, { ...old, [hashtagId]: input });
    },
  );
  a.debugLabel = `$HashtagPopularPost+${encodeURIComponent(hashtagId)}`;
  return a;
});

export const $HashtagRecentPost = safeAtomFamily((hashtagId: string) => {
  const a = atom(
    (get) => {
      const data = get($HashtagRecentPostStore)[hashtagId];
      if (!data) {
        // eslint-disable-next-line @typescript-eslint/no-throw-literal
        throw new Promise((resolve) => pendingQueue.push(resolve));
      }
      return data;
    },
    (get, set, input: PromiseOrValue<HashtagPosts>) => {
      const old = get($HashtagRecentPostStore);
      set($HashtagRecentPostStore, { ...old, [hashtagId]: input });
    },
  );

  a.debugLabel = `$HashtagRecentPost+${encodeURIComponent(hashtagId)}`;
  return a;
});

export const $HashtagRecentPostLoadmore = safeAtomFamily((hashtagId: string) => {
  const a = atom<null, [HashtagPosts], void>(null, async (get, set, input: HashtagPosts) => {
    const old = get($HashtagRecentPostStore);
    const hashtag = await old[hashtagId];
    if (hashtag) {
      set($HashtagRecentPostStore, {
        ...old,
        [hashtagId]: {
          ...hashtag,
          ...input,
          items: [...hashtag.items, ...input.items],
        },
      });
    }
  });

  a.debugLabel = `$HashtagRecentPostLoadmore+${encodeURIComponent(hashtagId)}`;
  return a;
});

export const $HashtagRelated = safeAtomFamily((hashtagId: string) => {
  const a = atom(
    (get) => {
      const data = get($HashtagRelatedStore)[hashtagId];
      if (!data) {
        // eslint-disable-next-line @typescript-eslint/no-throw-literal
        throw new Promise((resolve) => pendingQueue.push(resolve));
      }
      return data;
    },
    (get, set, input: PromiseOrValue<HashtagRelated>) => {
      const old = get($HashtagRelatedStore);
      set($HashtagRelatedStore, { ...old, [hashtagId]: input });
    },
  );
  a.debugLabel = `$HashtagRelated+${encodeURIComponent(hashtagId)}`;
  return a;
});
